import logoWhiteSuperAdmin from "@/assets/logoWithName-white-color-superadmin.svg";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from "@babackapp/ui/components/sidebar";
import {
  AlertTriangle,
  ArrowLeft,
  BellRing,
  ExternalLink,
  Fingerprint,
  History,
  LayoutDashboard,
  Lock,
  type LucideIcon,
  Puzzle,
  RotateCcw,
  ShieldCheck,
  Sparkles,
  Store,
  UserCog,
  Wrench,
} from "lucide-react";
import { Link, useLocation, useMatch } from "react-router-dom";
import { AdminShopSelector } from "./admin-shop-selector";

type AdminNavItem = {
  /** Relative to /admin. */
  to: string;
  label: string;
  icon: LucideIcon;
};

type AdminNavSection = {
  label: string;
  items: AdminNavItem[];
};

/**
 * Global (shop-independent) sections of the super admin. Shop-scoped pages
 * live under /admin/shops/:shopId, listed by shopNavigation().
 */
const ADMIN_NAVIGATION: AdminNavSection[] = [
  {
    label: "Vue d'ensemble",
    items: [
      { to: "dashboard", label: "Dashboard", icon: LayoutDashboard },
      { to: "shops", label: "Boutiques", icon: Store },
    ],
  },
  {
    label: "Monitoring",
    items: [
      { to: "events", label: "Journal & alertes", icon: BellRing },
      {
        to: "carrier-issues",
        label: "Problèmes transporteurs",
        icon: AlertTriangle,
      },
    ],
  },
  {
    label: "Returns",
    items: [
      {
        to: "replay-creation-actions",
        label: "Rejouer actions",
        icon: RotateCcw,
      },
      { to: "customer-portal", label: "Portail client", icon: ShieldCheck },
      { to: "storefront-embed", label: "Migration embed", icon: Puzzle },
      {
        to: "product-recommendations",
        label: "Reco produit",
        icon: Sparkles,
      },
    ],
  },
  {
    label: "RGPD",
    items: [
      { to: "pii-vault", label: "Coffre RGPD", icon: Lock },
      { to: "pii-vault/repair", label: "Coffre : reprise", icon: Wrench },
    ],
  },
  {
    label: "Accès",
    items: [{ to: "super-admins", label: "Super admins", icon: UserCog }],
  },
];

const TRACK_URL = import.meta.env.VITE_APP_TRACK_URL?.replace(/\/$/, "");
const TRACK_ADMIN_URL = TRACK_URL ? `${TRACK_URL}/admin` : null;

type ShopNavItem = {
  href: string;
  label: string;
  icon: LucideIcon;
  /** Another app, in a new tab: the admin stays where it is. */
  isExternal?: boolean;
};

/**
 * Pages of the selected shop. Every page shown here stays in the admin; the
 * Returns and Tracking apps are explicit exits, in a new tab.
 */
function shopNavigation(shopId: string): ShopNavItem[] {
  return [
    { href: `/admin/shops/${shopId}`, label: "Fiche", icon: Store },
    {
      href: `/admin/shops/${shopId}/events`,
      label: "Journal & alertes",
      icon: BellRing,
    },
    {
      href: `/admin/shops/${shopId}/access-log`,
      label: "Journal d'accès Baback",
      icon: History,
    },
    {
      href: `/admin/shops/${shopId}/product-recommendations`,
      label: "Reco produit",
      icon: Sparkles,
    },
    {
      href: `/admin/shops/${shopId}/pii-lookup`,
      label: "Données personnelles",
      icon: Fingerprint,
    },
    {
      href: `/shops/${shopId}`,
      label: "Ouvrir dans Returns",
      icon: ExternalLink,
      isExternal: true,
    },
    ...(TRACK_URL
      ? [
          {
            href: `${TRACK_URL}/shops/${shopId}`,
            label: "Ouvrir dans Tracking",
            icon: ExternalLink,
            isExternal: true,
          },
        ]
      : []),
  ];
}

function ShopNavLink({
  item: { href, label, icon: Icon, isExternal },
  isActive,
}: {
  item: ShopNavItem;
  isActive: boolean;
}) {
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip={label}>
        {isExternal ? (
          <a href={href} target="_blank" rel="noreferrer">
            <Icon />
            <span>{label}</span>
          </a>
        ) : (
          <Link to={href}>
            <Icon />
            <span>{label}</span>
          </Link>
        )}
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function AdminSidebar() {
  const { pathname } = useLocation();
  const { state } = useSidebar();
  const shopId = useMatch("/admin/shops/:shopId/*")?.params.shopId ?? null;

  return (
    <Sidebar collapsible="icon" variant="inset">
      <SidebarHeader className={state === "collapsed" ? "items-center" : ""}>
        <Link to="/admin" className="py-2">
          <img
            className="h-8 w-auto"
            src={logoWhiteSuperAdmin}
            alt="Baback Super admin"
          />
        </Link>
        <AdminShopSelector shopId={shopId} />
      </SidebarHeader>
      <SidebarContent>
        {shopId && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/80">
              Boutique
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {shopNavigation(shopId)
                  .filter(({ isExternal }) => !isExternal)
                  .map((item) => (
                    <ShopNavLink
                      key={item.href}
                      item={item}
                      isActive={pathname === item.href}
                    />
                  ))}
              </SidebarMenu>
              {/* Exits to the other apps, apart from the admin pages. */}
              <SidebarMenu className="mt-2 border-t border-sidebar-border pt-2">
                {shopNavigation(shopId)
                  .filter(({ isExternal }) => isExternal)
                  .map((item) => (
                    <ShopNavLink key={item.href} item={item} isActive={false} />
                  ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {/* A shop selected: its pages only, the global sections come back
            with "Vue globale" in the selector. */}
        {!shopId &&
          ADMIN_NAVIGATION.map((section) => (
            <SidebarGroup key={section.label}>
              <SidebarGroupLabel className="text-sidebar-foreground/80">
                {section.label}
              </SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map(({ to, label, icon: Icon }) => {
                    const href = `/admin/${to}`;
                    return (
                      <SidebarMenuItem key={to}>
                        <SidebarMenuButton
                          asChild
                          isActive={pathname === href}
                          tooltip={label}
                        >
                          <Link to={href}>
                            <Icon />
                            <span>{label}</span>
                          </Link>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))}
        {!shopId && TRACK_ADMIN_URL && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-sidebar-foreground/80">
              Tracking
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip="Admin Tracking">
                    <a href={TRACK_ADMIN_URL} target="_blank" rel="noreferrer">
                      <ExternalLink />
                      <span>Admin Tracking</span>
                    </a>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild tooltip="Retour à Returns">
              {/* "/" lands on the super admin default shop. */}
              <Link to="/">
                <ArrowLeft />
                <span>Retour à Returns</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
